import { useEffect, useState } from 'react'
import { apiURL } from '../../../utilities/URLs'
import { account } from '../../../classes/Account'
import { refreshAccessToken } from '../../../utilities/extensions'
import { useNavigate, useParams } from 'react-router-dom'
import { DayPicker } from 'react-day-picker'
import { DateTime } from 'luxon'
import 'react-day-picker/dist/style.css'
import { Agent } from './classes/Agent'
import { InlineWidget, useCalendlyEventListener } from 'react-calendly'

export default function BookMeeting() {
  const { applicationId } = useParams()
  const navigate = useNavigate()

  const [fetchingInformation, setFetchingInformation] = useState(true)
  const [agent, setAgent] = useState(new Agent())
  const [availability, setAvailability] = useState({})
  const [meetingURL, setMeetingURL] = useState('')
  const [selectedDate, setSelectedDate] = useState(null)
  const [selectedTime, setSelectedTime] = useState(null)
  const [showCalendly, setShowCalendly] = useState(false)
  const [scheduledMeeting, setScheduledMeeting] = useState(false)

  useCalendlyEventListener({
    onEventScheduled: () => setScheduledMeeting(true)
  })

  const toUTCDateString = (date) => {
    return DateTime.fromJSDate(date).toUTC().toISODate()
  }

  const availableDates = Object.keys(availability).filter(d => d !== 'traceId')
  const bookedDays = availableDates.map(dateStr => {
    const [year, month, day] = dateStr.split('-')
    return new Date(+year, +month - 1, +day)
  })

  const handleDateSelect = (dateObj) => {
    if (!dateObj) return
    const utcDateString = toUTCDateString(dateObj)
    if (availableDates.includes(utcDateString)) {
      setSelectedDate(utcDateString)
      setSelectedTime(null)
    }
  }

  const formatToCalendlyTime = (isoTime) => {
    const dt = DateTime.fromISO(isoTime).setZone(DateTime.local().zoneName)
    const offset = dt.toFormat('ZZ')
    return dt.toFormat("yyyy-MM-dd'T'HH:mm:ss") + offset
  }

  useEffect(() => {
    const handleGetInformation = async () => {
      await fetchAvailableSlots()
      setFetchingInformation(false)
    }

    const fetchAvailableSlots = async () => {
      const availibleSlotsResponse = await fetch(`${apiURL}/api/applications/${applicationId}/book-meeting/`, {
        method: 'GET',
        headers: {
          'Authorization': account.accessToken
        }
      })
      const availibleSlotsData = await availibleSlotsResponse.json()
      if (availibleSlotsData.response.error) {
        if (availibleSlotsData.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          refreshedToken && refreshedToken.length > 0
            ? await fetchAvailableSlots()
            : navigate('/', { state: { expired: true } })
        } else {
          console.error(availibleSlotsData.response.error)
        }
      } else {
        setAgent(availibleSlotsData.response.agent)
        setAvailability(availibleSlotsData.response.availableSlots)
        setMeetingURL(availibleSlotsData.response.meetingURL)
      }
    }

    handleGetInformation()
  }, [])

  return (
    <div className='w-11/12 flex flex-1 flex-col items-center'>
      <p className='font-bold text-2xl mt-4'>Book Meeting</p>
      {!showCalendly ? (
        <div className='bg-white shadow-lg rounded-xl p-6 w-full max-w-5xl'>
          {fetchingInformation ? (
            <div className='flex flex-row items-center justify-center gap-2 mt-5'>
              <div className='loader ease-linear rounded-full border-[6px] border-t-[6px] border-gray-200 dark:border-slate-700 border-t-ertc-primary dark:border-t-ertc-primary h-8 w-8 animate-spin'></div>
              <p className='text-md text-center'>Fetching Calendar...</p>
            </div>
          ) : (
            <div className='flex flex-col lg:flex-row gap-8 justify-center items-start'>
              <div className='mx-auto lg:mx-0'>
                <h2 className='text-lg font-semibold mb-2 text-center lg:text-left'>Select a Date</h2>
                <div className='border rounded-lg p-4 shadow-sm bg-gray-50'>
                  <DayPicker
                    mode='single'
                    selected={
                      selectedDate
                        ? new Date(
                            +selectedDate.split('-')[0],
                            +selectedDate.split('-')[1] - 1,
                            +selectedDate.split('-')[2]
                          )
                        : null
                    }
                    onSelect={handleDateSelect}
                    modifiers={{ available: bookedDays }}
                    modifiersStyles={{
                      available: {
                        fontWeight: 'bold',
                        backgroundColor: '#e0f2fe',
                        color: '#0369a1',
                      },
                    }}
                    className='text-base'
                    styles={{ root: { fontSize: '1.1rem' } }}
                    disabled={(date) => {
                      const iso = toUTCDateString(date)
                      return !availableDates.includes(iso)
                    }}
                  />
                </div>
              </div>

              <div className='w-full lg:w-1/2'>
                {selectedDate && availability[selectedDate] && (
                  <div>
                    <h2 className='text-lg font-semibold mb-2'>Available Times</h2>
                    <div className='grid grid-cols-3 sm:grid-cols-4 gap-3'>
                      {availability[selectedDate].map((time) => {
                        const dt = DateTime.fromISO(time, { zone: 'utc' }).setZone(DateTime.local().zoneName)
                        const display = dt.toLocaleString(DateTime.TIME_SIMPLE)
                        const fullTime = dt.toLocaleString(DateTime.DATETIME_MED)

                        return (
                          <button
                            key={time}
                            onClick={() => setSelectedTime(time)}
                            title={fullTime}
                            className={`p-2 rounded-lg border text-sm transition ${
                              selectedTime === time
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 hover:bg-blue-100'
                            }`}
                          >
                            {display}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )}

                {selectedTime && (
                  <div className='pt-6'>
                    <button
                      onClick={() => setShowCalendly(true)}
                      className='w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition'
                    >
                      Confirm Booking
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        !scheduledMeeting ? (
          <InlineWidget
            className='w-full h-full'
            url={`${meetingURL}/${formatToCalendlyTime(selectedTime)}`}
            prefill={{
              email: account.email,
              name: account.name,
              guests: [
                agent.email,
                'kurt@ameriwestusa.com',
                'paul@ertcrefund.com'
              ]
            }}
          />
        ) : (
          <div className='w-full h-full flex flex-col items-center justify-center'>
            <p className='text-center text-xl font-semibold mb-4'>You have officially booked your meeting!</p>
            <p className='text-center text-md mb-6'>You should receive an email shortly from Calendly with your meeting details.</p>
            <button 
              onClick={() => navigate('/dashboard')}
              className='bg-ertc-primary text-white px-4 py-2 rounded-md shadow-md'
            >
              Return Home
            </button>
          </div>
        )
      )}
    </div>
  )
}
